import {useMemo, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {Plus} from 'lucide-react'
import {useAuth} from '@/contexts/auth-context'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {DelegationFormDialog} from './DelegationFormDialog'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Strong} from '@/components/shared/Strong'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {formatDateOnly} from '@/lib/date-utils'
import type {Delegation} from '@/types/delegation'
import {DelegationStatus, DelegationStatusBadge} from '@/types/delegation'
import {Icon} from '@/components/shared/Icon'
import {MutedText} from '@/components/shared/MutedText'
import {PageLayout, TabPanelContent} from '@/components/shared/PageLayout'
import type {TabDefinition} from '@/components/shared/PageLayout'
import {MyDelegationsTabId} from '@/testids/MyDelegationsTestIds'
import {Pagination} from '@/components/ui/pagination'
import {SortDir, useServerPagination} from '@/hooks/use-server-pagination'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {MyDelegationControllerSearchMyDelegationsEndpoint} from '@/types/endpoint-contracts'

export function MyDelegations() {
    const i18n = useI18n()
    const services = useServices()
    const {user} = useAuth()

    const [revokeTarget, setRevokeTarget] = useState<Delegation | null>(null)
    const createDialog = useDialog()
    const [activeTab, setActiveTab] = useState<string>(MyDelegationsTabId.Outgoing)

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters,
        setSearchAndFilters,
    } = useServerPagination({
        endpoint: MyDelegationControllerSearchMyDelegationsEndpoint,
        defaultSortBy: MyDelegationControllerSearchMyDelegationsEndpoint.sort.START_AT,
        defaultSortDir: SortDir.DESC,
    })

    const searchQuery = useQuery({
        queryKey: ['my-delegations', 'search', queryString],
        queryFn: () => services.delegations.mySearch(
            Object.fromEntries(new URLSearchParams(queryString))),
        enabled: !!user,
    })

    const callerIdentityId = user?.identityId
    const allRows = useMemo(
        () => searchQuery.data?.content ?? [],
        [searchQuery.data])
    const outgoingRows = useMemo(
        () => allRows.filter((d) => d.delegator.id === callerIdentityId),
        [allRows, callerIdentityId])
    const incomingRows = useMemo(
        () => allRows.filter((d) => d.deputy.id === callerIdentityId),
        [allRows, callerIdentityId])

    const revokeMutation = useEntityMutation({
        mutationFn: (id: string) => services.delegations.myRevoke(id),
        invalidate: ['my-delegations'],
        successMessage: i18n.delegations.revokeSuccess,
        closeDialog: () => setRevokeTarget(null),
    })

    const isLoading = searchQuery.isLoading
    const error = searchQuery.error

    const tabs: TabDefinition[] = [
        {
            key: MyDelegationsTabId.Outgoing,
            label: String(i18n.delegations.outgoing),
            count: outgoingRows.length > 0 ? outgoingRows.length : undefined,
        },
        {
            key: MyDelegationsTabId.Incoming,
            label: String(i18n.delegations.incoming),
            count: incomingRows.length > 0 ? incomingRows.length : undefined,
        },
    ]

    return (
        <PageLayout
            data-testid="my-delegations-page"
            variant="tabbed-list"
            icon={CONCEPT_ICON.delegation}
            title={String(i18n.delegations.myDelegations)}
            description={String(i18n.delegations.myDelegationsDesc)}
            loading={isLoading}
            error={error}
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            actions={
                <Button onClick={createDialog.open} data-testid="create-delegation-btn">
                    <Icon icon={Plus} size="md" className="mr-2"/>
                    {i18n.delegations.createDelegation}
                </Button>
            }
        >
            {activeTab === MyDelegationsTabId.Outgoing && (
                <TabPanelContent
                    data-testid="outgoing-delegations-section"
                    filters={
                        <UnifiedSearchBar
                            contract={MyDelegationControllerSearchMyDelegationsEndpoint}
                            value={searchAndFilters}
                            onChange={setSearchAndFilters}
                            placeholder={String(i18n.delegations.searchPlaceholder)}
                            testIdNamespace="my-delegations"
                        />
                    }
                    pagination={searchQuery.data && searchQuery.data.totalElements > 0 ? (
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalItems={searchQuery.data.totalElements}
                            totalPages={searchQuery.data.totalPages}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            pageSizeOptions={pageSizeOptions}
                        />
                    ) : undefined}
                >
                    {outgoingRows.length === 0 && (
                        <MutedText block centered className="py-4"><MutedText>{i18n.delegations.noOutgoing}</MutedText></MutedText>
                    )}
                    {outgoingRows.length > 0 && (
                        <DelegationTable
                            delegations={outgoingRows}
                            showDeputy
                            i18n={i18n}
                            onRevoke={setRevokeTarget}
                        />
                    )}
                </TabPanelContent>
            )}

            {activeTab === MyDelegationsTabId.Incoming && (
                <TabPanelContent
                    data-testid="incoming-delegations-section"
                    filters={
                        <UnifiedSearchBar
                            contract={MyDelegationControllerSearchMyDelegationsEndpoint}
                            value={searchAndFilters}
                            onChange={setSearchAndFilters}
                            placeholder={String(i18n.delegations.searchPlaceholder)}
                            testIdNamespace="my-delegations"
                        />
                    }
                    pagination={searchQuery.data && searchQuery.data.totalElements > 0 ? (
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalItems={searchQuery.data.totalElements}
                            totalPages={searchQuery.data.totalPages}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            pageSizeOptions={pageSizeOptions}
                        />
                    ) : undefined}
                >
                    {incomingRows.length === 0 && (
                        <MutedText block centered className="py-4"><MutedText>{i18n.delegations.noIncoming}</MutedText></MutedText>
                    )}
                    {incomingRows.length > 0 && (
                        <DelegationTable
                            delegations={incomingRows}
                            showDelegator
                            i18n={i18n}
                        />
                    )}
                </TabPanelContent>
            )}

            {/* Revoke Confirmation Dialog */}
            <Dialog open={!!revokeTarget} onOpenChange={() => setRevokeTarget(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.delegations.revokeDelegation}</DialogTitle>
                        <DialogDescription>{i18n.delegations.revokeConfirmation}</DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="revoke"
                        onCancel={() => setRevokeTarget(null)}
                        saving={revokeMutation.isPending}
                        saveLabel={i18n.delegations.revokeDelegation}
                        destructive
                        saveType="button"
                        onSave={() => revokeTarget && revokeMutation.mutate(revokeTarget.id)}
                    />
                </DialogContent>
            </Dialog>

            {/* Create Delegation Dialog */}
            <DelegationFormDialog open={createDialog.isOpen} onOpenChange={createDialog.bind.onOpenChange}/>
        </PageLayout>
    )
}

interface DelegationTableProps {
    delegations: Delegation[]
    showDelegator?: boolean
    showDeputy?: boolean
    i18n: ReturnType<typeof useI18n>
    onRevoke?: (delegation: Delegation) => void
}

function DelegationTable({delegations, showDelegator, showDeputy, i18n, onRevoke}: DelegationTableProps) {
    const {workflowSteps} = useAppConfig()

    const sorted = [...delegations].sort((a, b) => {
        const nameA = showDeputy ? a.deputy.displayName : a.delegator.displayName
        const nameB = showDeputy ? b.deputy.displayName : b.delegator.displayName
        return nameA.localeCompare(nameB)
    })

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    {showDelegator && <TableHead data-testid="col-delegator">{i18n.delegations.columns.delegator}</TableHead>}
                    {showDeputy && <TableHead data-testid="col-deputy">{i18n.delegations.columns.deputy}</TableHead>}
                    <TableHead data-testid="col-workflow-step">{i18n.delegations.columns.workflowStep}</TableHead>
                    <TableHead data-testid="col-status">{i18n.delegations.columns.status}</TableHead>
                    <TableHead data-testid="col-start-at">{i18n.delegations.columns.startAt}</TableHead>
                    <TableHead data-testid="col-end-at">{i18n.delegations.columns.endAt}</TableHead>
                    <TableHead data-testid="col-reason">{i18n.delegations.columns.reason}</TableHead>
                    {onRevoke && <TableHead data-testid="col-actions"/>}
                </TableRow>
            </TableHeader>
            <TableBody>
                {sorted.map((d) => (
                    <TableRow key={d.id}>
                        {showDelegator && (
                            <TableCell>
                                <Strong>{d.delegator.displayName}</Strong>
                            </TableCell>
                        )}
                        {showDeputy && (
                            <TableCell>
                                <Strong>{d.deputy.displayName}</Strong>
                            </TableCell>
                        )}
                        <TableCell>{labelOf(workflowSteps, d.workflowStepCode)}</TableCell>
                        <TableCell>
                            <Badge variant={DelegationStatusBadge.of(d.status)?.variant ?? BadgeVariant.OUTLINE}>
                                {i18n.delegations.statuses[d.status]}
                            </Badge>
                        </TableCell>
                        <TableCell>{d.permanent ? <Badge variant="outline">{i18n.delegations.permanent}</Badge> : formatDateOnly(d.startAt)}</TableCell>
                        <TableCell>{d.permanent ? '\u2014' : formatDateOnly(d.endAt)}</TableCell>
                        <TableCell>{d.reason ?? '\u2014'}</TableCell>
                        {onRevoke && (
                            <TableCell>
                                {(d.status === DelegationStatus.SCHEDULED || d.status === DelegationStatus.ACTIVE) && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => onRevoke(d)}
                                        data-testid={`revoke-delegation-${d.id}`}
                                    >
                                        {i18n.delegations.revokeDelegation}
                                    </Button>
                                )}
                            </TableCell>
                        )}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    )
}
